// components/tour/TourWelcome.jsx
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Avatar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  School as SchoolIcon,
  PlayArrow as PlayIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTour } from './TourContext';
import { getDecodedToken } from '../../utils/auth';

const TourWelcome = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { startWelcome, skipTour, tourConfig } = useTour();

  // Get username from token
  const decodedToken = getDecodedToken();
  const username = decodedToken?.user?.username || 'there';

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10002,
        p: 2
      }}
    >
      <Paper
        elevation={16}
        sx={{
          maxWidth: isMobile ? 350 : 450,
          width: '100%',
          p: isMobile ? 3 : 4,
          borderRadius: 3,
          textAlign: 'center',
          backgroundColor: 'background.paper',
          border: `2px solid ${theme.palette.primary.main}`,
          boxShadow: `
            0 16px 64px rgba(0, 0, 0, 0.4),
            0 0 0 1px rgba(33, 150, 243, 0.2)
          `,
          animation: 'slideIn 0.4s ease-out',
          '@keyframes slideIn': {
            from: {
              opacity: 0,
              transform: 'translateY(-20px) scale(0.95)'
            },
            to: {
              opacity: 1,
              transform: 'translateY(0) scale(1)'
            }
          }
        }}
      >
        {/* Welcome Icon */}
        <Avatar
          sx={{
            width: isMobile ? 64 : 80,
            height: isMobile ? 64 : 80,
            bgcolor: theme.palette.primary.main,
            mx: 'auto',
            mb: 3,
            boxShadow: theme.shadows[8]
          }}
        >
          <SchoolIcon sx={{ fontSize: isMobile ? '2rem' : '2.5rem' }} />
        </Avatar>

        {/* Welcome Text */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            fontSize: isMobile ? '1.5rem' : '2rem'
          }}
        >
          Welcome, {username}! 👋
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            color: 'primary.main',
            mb: 2,
            fontSize: isMobile ? '1rem' : '1.25rem'
          }}
        >
          {tourConfig?.title || 'Let\'s get you started!'}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            mb: 4,
            lineHeight: 1.6,
            fontSize: isMobile ? '0.875rem' : '1rem'
          }}
        >
          We'll show you around your language learning dashboard so you can make the most of your learning journey. This quick tour will highlight all the key features.
        </Typography>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
          <Button
            variant="outlined"
            onClick={skipTour}
            startIcon={<CloseIcon />}
            size={isMobile ? 'medium' : 'large'}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: 500,
              py: isMobile ? 1.5 : 2,
              fontSize: isMobile ? '0.875rem' : '1rem'
            }}
          >
            Skip Tour
          </Button>
          <Button
            variant="contained"
            onClick={startWelcome}
            startIcon={<PlayIcon />}
            size={isMobile ? 'medium' : 'large'}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: 600,
              py: isMobile ? 1.5 : 2,
              fontSize: isMobile ? '0.875rem' : '1rem',
              boxShadow: theme.shadows[4],
              '&:hover': {
                boxShadow: theme.shadows[8]
              }
            }}
          >
            Start Tour
          </Button>
        </Box>

        {/* Tour Info */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            mt: 3,
            opacity: 0.7,
            fontSize: '0.75rem'
          }}
        >
          Takes about 2 minutes • {tourConfig?.steps?.length || 0} steps
        </Typography>
      </Paper>
    </Box>
  );
};

export default TourWelcome;